import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Upload } from 'react-feather';
import styled from '@emotion/styled';
import { Switch } from '@mui/material';
import { createId } from '@paralleldrive/cuid2';
import type { RemirrorJSON } from '@remirror/core';
import {
  ChatContextType,
  ALLOWED_INPUT_ATTACHMENT_MIME_TYPES,
  INPUT_ATTACHMENT_MAX_FILES_PER_MESSAGE,
  maxInputAttachmentSizeBytes,
} from '@trinity/chat';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import type { PendingAttachmentsState } from '@trinity/chat-components/components/Chat.State/interfaces.pending-attachments';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { InteractionModeDropdown } from '@trinity/chat-components/components/DigitalTwin/InteractionModeDropdown';
import { SoundInput } from '@trinity/chat-components/components/icons/SoundInput';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import {
  useChatInteractionModes,
  useHasChatVoiceInputEnabled,
  useHasChatVoiceModeEnabled,
} from '@trinity/chat-components/containers/Chat/selectors';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { pulse } from '@trinity/chat-components/utils';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';
import { useSelectedUserTerritoryId } from '../Chat.Filters/hooks/useSelectedUserTerritoryId';
import {
  SubmitMessageContext,
  ChatHasAccessContext,
  ChatInputFeaturesContext,
  ActiveChatSessionContext,
  EditorDocContext,
  ChatSessionPendingAttachmentsContext,
} from './context';
import { useAttachmentExtractionStatus } from './hooks/useAttachmentExtractionStatus';
import { useCancelQueryMutation } from './hooks/useCancelQueryMutation';
import type { MicState } from './hooks/useSpeechRecognition';
import { useUploadInputAttachment } from './hooks/useUploadInputAttachment';
import { StagedFiles } from './Input.StagedFiles';
import type { AtMentionContextValue } from './RemirrorInput/AtMentionContext';
import type { RemirrorChatInputRef } from './RemirrorInput/RemirrorChatInput';
import { RemirrorChatInput } from './RemirrorInput/RemirrorChatInput';

const MIME_TYPE_DISPLAY_NAMES: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'audio/mpeg': 'MP3',
  'video/mp4': 'MP4',
  'text/csv': 'CSV',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
};

type VoiceMode = 'text' | 'voice';

type UnsubmittedMessageProps = {
  isSidePanel?: boolean;
};

export const UnsubmittedMessage = ({ isSidePanel }: UnsubmittedMessageProps = {}) => {
  const { remirrorRef, soundLevelRef, speechRecognition, onClickMicrophone } =
    useContext(InputControlsContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { state: submitState, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const hasChatVoiceInputEnabled = useHasChatVoiceInputEnabled();
  const features = useGroupFeatures();
  const inputFeatures = useContext(ChatInputFeaturesContext);
  const showInputAttachments = features.chatInputAttachmentsEnabled && inputFeatures.fileUpload !== false;

  const acceptedMimeTypes =
    (features?.chatInputAttachmentAllowedMimeTypes?.length ?? 0) > 0
      ? features.chatInputAttachmentAllowedMimeTypes
      : ALLOWED_INPUT_ATTACHMENT_MIME_TYPES;
  const pendingAttachments = useContext(ChatSessionPendingAttachmentsContext);
  const remainingAttachmentSlots = INPUT_ATTACHMENT_MAX_FILES_PER_MESSAGE - pendingAttachments.length;
  const attachTooltip = useMemo(
    () =>
      remainingAttachmentSlots > 0
        ? `Up to ${INPUT_ATTACHMENT_MAX_FILES_PER_MESSAGE} files. Supported formats: ${acceptedMimeTypes.map((mt) => MIME_TYPE_DISPLAY_NAMES[mt] ?? mt).join(', ')}`
        : `Limit of ${INPUT_ATTACHMENT_MAX_FILES_PER_MESSAGE} files per message reached`,
    [acceptedMimeTypes, remainingAttachmentSlots]
  );
  const chat = useContext(ActiveChatSessionContext);
  const editorDocRef = useContext(EditorDocContext);
  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const applicationIdentifier = useActiveApplicationIdentifier();
  const uploadInputAttachment = useUploadInputAttachment();
  const territoryId = useSelectedUserTerritoryId();

  useAttachmentExtractionStatus();

  const hasAccess = useContext(ChatHasAccessContext);

  const stagedFilesInputRef = useRef<HTMLInputElement>(null);
  const [showStagedFiles, setShowStagedFiles] = useState(true);

  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();
  const settingsInteractionModes = useChatInteractionModes();
  const { isDigitalTwin, isNewChatInteractionLayout } = useAvatarPanel();

  const showInteractionButtons = Boolean(
    inputFeatures.voiceInput !== false &&
    (isDigitalTwin ||
      (isNewChatInteractionLayout && settingsInteractionModes.available.length > 1) ||
      hasVoiceModeFeature)
  );

  const inputDisabled = useMemo(() => {
    return !hasAccess || submitState.status === 'submitting-query' || submitState.status === 'uploading-file';
  }, [submitState.status, hasAccess]);

  const micDisabled = useMemo(() => {
    return !hasAccess || submitState.status === 'submitting-query' || submitState.status === 'uploading-file';
  }, [hasAccess, submitState.status]);

  const uploadAndExtract = useCallback(
    async (attachment: PendingAttachmentsState.PendingAttachment) => {
      const maxBytes = maxInputAttachmentSizeBytes(attachment.mimeType);
      if (attachment.sizeBytes > maxBytes) {
        dispatch({
          type: 'pending-attachment/upload-failed',
          payload: {
            clientId: attachment.clientId,
            errorMessage: `File exceeds the ${Math.floor(maxBytes / 1024 / 1024)} MB limit`,
          },
        });
        return;
      }

      try {
        const { uploads } = await apiClient.chat.getInputAttachmentUploadCredentials({
          files: [{ name: attachment.name, mimeType: attachment.mimeType, sizeBytes: attachment.sizeBytes }],
        });
        const [upload] = uploads;
        dispatch({
          type: 'pending-attachment/upload-started',
          payload: { clientId: attachment.clientId, identifier: upload.identifier },
        });
        await uploadInputAttachment(attachment.file, upload);

        const { status } = await apiClient.chat.extractInputAttachment({
          identifier: upload.identifier,
          fileUri: `s3://${upload.bucket}/${upload.s3Key}`,
          mimeType: attachment.mimeType,
        });

        dispatch({
          type: 'pending-attachment/extraction-updated',
          payload: { identifier: upload.identifier, status },
        });
      } catch {
        dispatch({
          type: 'pending-attachment/upload-failed',
          payload: { clientId: attachment.clientId, errorMessage: 'Failed to upload file' },
        });
      }
    },
    [apiClient.chat, dispatch, uploadInputAttachment]
  );

  const handleFileStaged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      // Files past the per-message limit are dropped; the attach tooltip states the limit.
      for (const file of Array.from(files).slice(0, Math.max(remainingAttachmentSlots, 0))) {
        const clientId = createId();
        dispatch({
          type: 'pending-attachment/file-staged',
          payload: { clientId, file, name: file.name, mimeType: file.type, sizeBytes: file.size },
        });
        // Upload immediately so extraction has something in S3 to work on well
        // before the user finishes typing.
        uploadAndExtract({
          clientId,
          identifier: '',
          name: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          file,
          status: 'staged',
          progress: 0,
        }).catch(() => undefined);
      }
      e.target.value = '';
    },
    [dispatch, remainingAttachmentSlots, uploadAndExtract]
  );

  const handleRetry = useCallback(
    (attachment: PendingAttachmentsState.PendingAttachment) => {
      uploadAndExtract(attachment).catch(() => undefined);
    },
    [uploadAndExtract]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      // Remirror handles Shift+Enter natively (inserts newline)
      return;
    }
  }, []);

  const activeContextIdentifier = useMemo(() => {
    if (chat.context.type === ChatContextType.Agent) {
      return chat.context.data.identifier;
    }
    return null;
  }, [chat.context]);

  const activeContextData = useMemo(() => {
    if (chat.context.type !== ChatContextType.Agent) return null;
    return chat.context.data;
  }, [chat.context]);

  const mentionsEnabled = true;

  const autocompleteEnabled = useMemo(() => {
    return mentionsEnabled && (activeContextData?.subjectAutocomplete?.chatAutocompleteEnabled ?? false);
  }, [mentionsEnabled, activeContextData]);

  const atMentionContextValue = useMemo((): AtMentionContextValue | undefined => {
    if (!autocompleteEnabled || !activeContextIdentifier || !applicationIdentifier) return undefined;
    return [
      activeContextIdentifier,
      async (query: string, pagination) => {
        const response = await apiClient.chatContext.getContextSubjectSearch({
          contextIdentifier: activeContextIdentifier,
          query,
          limit: pagination?.limit,
          offset: pagination?.offset,
          territoryId,
        });

        return {
          results: response.results
            .map((item) => ({
              entityId: item.entityId ?? item.name,
              objectId: item.objectId,
              name: item.name,
              type: item.type,
              link: item.link,
              subtitle: item.subtitle,
            }))
            .filter((item) => item.entityId !== undefined),
        };
      },
      activeContextData?.subjectAutocomplete?.displayConfig ?? undefined,
    ];
  }, [
    autocompleteEnabled,
    activeContextIdentifier,
    apiClient.chatContext,
    activeContextData,
    brandIdentifier,
    applicationIdentifier,
    territoryId,
  ]);

  const handleTextChange = useCallback(
    (text: string) => {
      dispatch({
        type: 'input-changed',
        payload: { value: text },
      });
    },
    [dispatch]
  );

  const handleDocChange = useCallback(
    (doc: RemirrorJSON) => {
      editorDocRef.current = doc;
    },
    [editorDocRef]
  );

  const localRemirrorRef = useRef<RemirrorChatInputRef | null>(null);
  const handleRemirrorRef = useCallback(
    (node: RemirrorChatInputRef | null) => {
      const prevInstance = localRemirrorRef.current;
      localRemirrorRef.current = node;

      if (remirrorRef) {
        if (node) {
          remirrorRef.current = node;
          return;
        }

        if (remirrorRef.current === prevInstance) {
          remirrorRef.current = null;
        }
      }
    },
    [remirrorRef]
  );

  const handleSubmitAndClear = useCallback(() => {
    handleSubmit();
    localRemirrorRef.current?.clearAndSetText('');
  }, [handleSubmit]);

  const micState = speechRecognition.micState;

  const showVoiceFeatures = useMemo(
    () => speechRecognition.isSupported() && hasChatVoiceInputEnabled && inputFeatures.voiceInput !== false,
    [speechRecognition, hasChatVoiceInputEnabled, inputFeatures.voiceInput]
  );

  const placeholder = speechRecognition.listening ? 'Speak your question.' : 'Type your question.';

  return (
    <Root>
      <InputWrap onKeyDown={handleKeyDown}>
        <HiddenFileInput
          ref={stagedFilesInputRef}
          type="file"
          accept={acceptedMimeTypes.join(',')}
          multiple
          onChange={handleFileStaged}
        />
        <InputGrid>
          <EditorContainer>
            <RemirrorChatInput
              key={mentionsEnabled ? 'mentions-enabled' : 'no-mentions'}
              ref={handleRemirrorRef}
              chrome={isSidePanel ? 'embedded' : 'standalone'}
              placeholder={placeholder}
              disabled={inputDisabled}
              onSubmit={handleSubmitAndClear}
              onTextChange={handleTextChange}
              onDocChange={handleDocChange}
              mentionsEnabled={mentionsEnabled}
              atMentionContextValue={atMentionContextValue}
              topContent={
                pendingAttachments.length > 0 && showStagedFiles ? (
                  <StagedFiles onRetry={handleRetry} />
                ) : undefined
              }
            />
          </EditorContainer>
          <RightSection>
            {showVoiceFeatures && (
              <Tooltip title={micState === 'error' ? speechRecognition.errorMsg : null} placement="top">
                <StyledMicButton
                  disabled={micDisabled || micState === 'error'}
                  micState={micState}
                  onClick={onClickMicrophone}
                >
                  <MicIconButton micState={micState} soundLevelRef={soundLevelRef} />
                </StyledMicButton>
              </Tooltip>
            )}
            <SubmitButton />
          </RightSection>
          {(showInputAttachments || pendingAttachments.length > 0) && (
            <BottomLeftButtons>
              {pendingAttachments.length > 0 && (
                <StagedFilesCountButton
                  count={pendingAttachments.length}
                  isOpen={showStagedFiles}
                  onClick={() => setShowStagedFiles((prev) => !prev)}
                />
              )}
              {showInputAttachments && (
                <Tooltip title={attachTooltip} placement="top">
                  <span>
                    <AttachFilesButton
                      type="button"
                      disabled={inputDisabled || remainingAttachmentSlots <= 0}
                      onClick={() => stagedFilesInputRef.current?.click()}
                      aria-label="Upload files"
                    >
                      <UploadIcon size={15} />
                      <UploadLabel>Upload</UploadLabel>
                    </AttachFilesButton>
                  </span>
                </Tooltip>
              )}
            </BottomLeftButtons>
          )}
          {showInteractionButtons && (
            <BottomRightButtons>
              <InteractionButtons />
            </BottomRightButtons>
          )}
        </InputGrid>
      </InputWrap>
    </Root>
  );
};

// ─── Voice Toggle Switch (replaces dropdown) ───
const VoiceToggleSwitch = () => {
  const voicemode = useContext(VoiceModeContext);
  const { onClickVoiceMode } = useContext(InputControlsContext);

  const currentMode: VoiceMode = voicemode?.enabled ? 'voice' : 'text';

  const handleChange = useCallback(() => {
    onClickVoiceMode();
  }, [onClickVoiceMode]);

  return (
    <VoiceSwitchContainer>
      <VoiceSwitchLabel>Voice</VoiceSwitchLabel>
      <StyledSwitch
        checked={currentMode === 'voice'}
        onChange={handleChange}
        inputProps={{ 'aria-label': 'Toggle voice mode' }}
      />
    </VoiceSwitchContainer>
  );
};

// ─── Microphone Icon Component ───
const MicrophoneIcon = ({ micState }: { micState: MicState }) => {
  if (micState === 'error') {
    return (
      <svg
        width="20px"
        height="20px"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ opacity: 0.5, color: '#808080' }}
      >
        <path
          d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3m5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg
      width="20px"
      height="20px"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color: '#808080' }}
    >
      <path
        d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3m5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72z"
        fill="currentColor"
      />
    </svg>
  );
};

// ─── Send Button Icon Component ───
const SendButtonIcon = ({ isLoading, isReadyToSubmit }: { isLoading: boolean; isReadyToSubmit: boolean }) => {
  const theme = useTheme();
  const readyColor = theme.palette.wl?.enabled ? theme.palette.wl.primary.text : 'var(--pri-01)';

  if (isLoading) {
    return (
      <svg
        width="16px"
        height="16px"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: '#808080' }}
      >
        <rect x="6" y="6" width="12" height="12" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg
      width="20px"
      height="20px"
      viewBox="0 0 15 15"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color: isReadyToSubmit ? readyColor : '#808080' }}
    >
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g fillRule="nonzero">
          <path
            d="M1.30622886,8.73264264 C0.856863395,10.6856906 0.0303349576,14.2770286 0.0218226647,14.3154327 C-0.104531683,14.8818188 0.341261655,15.1215849 0.754905887,14.9406853 L14.7350169,7.94795376 L14.7364229,7.94795376 C14.9031727,7.85620655 15,7.69087622 15,7.50015322 C15,7.30931902 14.9031727,7.14387748 14.7364229,7.05216734 L14.7350169,7.05216734 L0.754905887,0.0593245887 C0.341261655,-0.121612034 -0.104531683,0.118228152 0.0218226647,0.684651344 C0.0303729589,0.72301836 0.856863395,4.31428226 1.30622886,6.26744139 L8.60407594,7.50011615 L1.30622886,8.73264264 Z"
            fill="currentColor"
          />
        </g>
      </g>
    </svg>
  );
};

// ─── Voice Mode Dropdown (legacy, kept for reference) ───

const InteractionButtons = () => {
  const { isDigitalTwin, isNewChatInteractionLayout } = useAvatarPanel();
  const hasVoiceModeFeature = useHasChatVoiceModeEnabled();
  const settingsInteractionModes = useChatInteractionModes();
  const inputFeatures = useContext(ChatInputFeaturesContext);

  if (inputFeatures.voiceInput === false) return null;

  if (isDigitalTwin || (isNewChatInteractionLayout && settingsInteractionModes.available.length > 1)) {
    return <InteractionModeDropdown />;
  }

  if (hasVoiceModeFeature) {
    return <VoiceToggleSwitch />;
  }

  return null;
};

interface MicIconButtonProps {
  micState: MicState;
  soundLevelRef: React.RefObject<HTMLDivElement>;
}

const MicIconButton = ({ micState, soundLevelRef }: MicIconButtonProps) => {
  const theme = useTheme();

  if (micState === 'listening' || micState === 'waiting-for-permission') {
    const barColor = theme.palette.wl?.enabled ? theme.palette.wl.primary.text : 'var(--pri-01)';
    return <SoundInput ref={soundLevelRef} color={barColor} paused={micState === 'waiting-for-permission'} />;
  }
  return <MicrophoneIcon micState={micState} />;
};

const SubmitButton = () => {
  const { onClickSubmit } = useContext(InputControlsContext);
  const { state } = useContext(SubmitMessageContext);
  const cancelQuery = useCancelQueryMutation();

  const disabled = useMemo(() => {
    return ['disabled', 'uploading-file'].includes(state.status);
  }, [state.status]);

  const handleClick = useCallback(async () => {
    if (disabled) return;
    if (state.status === 'inactive') {
      return onClickSubmit();
    }
    if (state.status === 'submitting-query') {
      return await cancelQuery.mutateAsync();
    }
  }, [disabled, state.status, cancelQuery, onClickSubmit]);

  const isLoading = state.status === 'submitting-query';
  // 'inactive' means the message (or a submittable attachment) is present and the button
  // isn't otherwise disabled/uploading/submitting — i.e. ready to submit. Applied uniformly
  // across Side Panel, Chat Modal, and full-screen chat since they all share this component.
  const isReadyToSubmit = state.status === 'inactive';

  return (
    <Button
      aria-label="Submit message"
      disabled={disabled}
      isLoading={isLoading}
      isReadyToSubmit={isReadyToSubmit}
      onClick={handleClick}
    >
      <SendButtonIcon isLoading={isLoading} isReadyToSubmit={isReadyToSubmit} />
    </Button>
  );
};

const submitBtnSize = 38;
const actionButtonColor = '#e8e8e8';
const actionButtonHoverColor = '#d0d0d0';

const Root = styled('div')({
  // width: messageLaneWidth,
  width: '100%',
  margin: '0 auto',
  position: 'relative',
  // paddingTop: '70px',
});

// The input wrap container now always uses the enclosed styling across all chat interfaces
// (side panel, modal, full-page chat) so voice controls, submit button, and text input
// sit together inside the container box.
export const InputWrap = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 10,
  boxSizing: 'border-box',
  width: '100%',
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light2}`,
  borderRadius: 5,
  padding: '6px 8px 4px 8px',
  transition: 'border 0.3s',
  '&:hover': {
    border: `2px solid ${theme.palette.wl?.enabled ? theme.palette.wl.primary.main : 'var(--pri-02)'}`,
  },
  '&:focus-within': {
    border: `2px solid ${theme.palette.wl?.enabled ? theme.palette.wl.primary.main : 'var(--pri-02)'}`,
  },
}));

const InputGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gridTemplateRows: 'auto auto',
  columnGap: 8,
  rowGap: 2,
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
});

const EditorContainer = styled('div')({
  gridColumn: '1 / 2',
  gridRow: '1 / 2',
  minWidth: 0,
});

const RightSection = styled('div')({
  gridColumn: '2 / 3',
  gridRow: '1 / 2',
  justifySelf: 'center',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
});

const Button = styled('button', { shouldForwardProp: shouldForwardProp('isLoading', 'isReadyToSubmit') })<{
  isLoading?: boolean;
  isReadyToSubmit?: boolean;
}>(({ disabled, isLoading, isReadyToSubmit, theme }) => {
  let backgroundColor = actionButtonColor;
  let hoverBg = actionButtonHoverColor;
  if (disabled) {
    backgroundColor = actionButtonColor;
    hoverBg = actionButtonColor;
  }
  if (!disabled && isReadyToSubmit) {
    backgroundColor = theme.palette.wl?.enabled ? theme.palette.wl.primary.main : 'var(--pri-02)';
    hoverBg = theme.palette.wl?.enabled ? theme.palette.wl.primary.dark : 'var(--sec-02)';
  }
  if (!disabled && isLoading) {
    backgroundColor = '#f2f2f2';
    hoverBg = '#e6e6e6';
  }
  return {
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: submitBtnSize,
    height: submitBtnSize,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'background-color 0.3s, border 0.3s',
    backgroundColor,
    border: isLoading ? '2px solid #d4d8de' : '2px solid transparent',
    '&:hover:not([disabled])': {
      backgroundColor: hoverBg,
    },
  };
});

const VoiceSwitchContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const VoiceSwitchLabel = styled('span')({
  fontSize: 13,
  lineHeight: 1,
  color: '#808080',
  fontWeight: 700,
});

const StyledSwitch = styled(Switch)(({ theme }) => ({
  marginRight: -12, // MUI Switch root has 12px internal right padding; pull track flush with container right edge
  '& .MuiSwitch-switchBase': {
    color: theme.palette.gray.light1,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'var(--pri-02)',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: 'var(--pri-02)',
  },
}));

type StyledMicButtonProps = {
  micState: MicState;
};

const StyledMicButton = styled('button')<StyledMicButtonProps>(({ theme, micState, disabled }) => {
  const wl = theme.palette.wl?.enabled ? theme.palette.wl : null;
  // `pulse` parses its argument as a color, so it can't take a CSS variable like `var(--pri-02)`.
  const pulseColor = wl ? wl.primary.main : theme.palette.sentiment.primary.main;
  const activeBg = wl ? wl.primary.main : 'var(--pri-02)';

  return {
    cursor: disabled ? 'not-allowed' : 'pointer',

    width: `${submitBtnSize}px`,
    height: `${submitBtnSize}px`,
    borderRadius: '50%',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    transition: 'background-color 0.3s',

    boxShadow: `0 0 0 0 ${pulseColor}`,
    animation: micState === 'listening' ? `${pulse(pulseColor)} 4s infinite` : 'none',
    backgroundColor: micState === 'listening' || micState === 'waiting-for-permission' ? activeBg : 'none',

    '&:hover:not([disabled])': {
      backgroundColor: (() => {
        if (micState === 'listening' || micState === 'waiting-for-permission') return undefined;
        if (disabled) return theme.palette.gray.light1;
        return wl ? wl.primary.light : theme.palette.sentiment.primary.light;
      })(),
    },

    '&:hover:not([disabled]) svg': {
      color: wl ? wl.primary.dark : theme.palette.sentiment.primary.dark,
      //backgroundColor: listening ? undefined : disabled ? theme.palette.gray.light1 : theme.palette.sentiment.primary.light,
    },
  };
});

const BottomLeftButtons = styled('div')({
  gridColumn: '1 / 2',
  gridRow: '2 / 3',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 28,
});

const BottomRightButtons = styled('div')({
  gridColumn: '2 / 3',
  gridRow: '2 / 3',
  justifySelf: 'center',
  display: 'flex',
  alignItems: 'center',
  minHeight: 28,
});

const HiddenFileInput = styled('input')({
  display: 'none',
});

const AttachFilesButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  height: 28,
  padding: '0 10px',
  backgroundColor: theme.palette.wl?.enabled ? theme.palette.wl.primary.main : 'var(--pri-02)',
  border: `1px solid ${theme.palette.wl?.enabled ? theme.palette.wl.primary.main : 'var(--pri-02)'}`,
  borderRadius: 14,
  cursor: 'pointer',
  color: theme.palette.wl?.enabled ? theme.palette.wl.primary.text : 'var(--pri-01)',
  transition: 'background-color 0.2s, border-color 0.2s',
  ':hover:not([disabled])': {
    backgroundColor: theme.palette.wl?.enabled ? theme.palette.wl.primary.dark : 'var(--sec-02)',
  },
  ':disabled': {
    backgroundColor: theme.palette.gray.light1,
    opacity: 0.4,
    cursor: 'not-allowed',
  },
}));

const UploadIcon = styled(Upload)(({ theme }) => ({
  color: theme.palette.wl?.enabled ? theme.palette.wl.primary.text : 'var(--pri-01)',
}));

const UploadLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 12,
  color: theme.palette.wl?.enabled ? theme.palette.wl.primary.text : 'var(--pri-01)',
  lineHeight: 1,
}));

type StagedFilesCountButtonProps = {
  count: number;
  isOpen: boolean;
  onClick: () => void;
};

const StagedFilesCountButton = ({ count, isOpen, onClick }: StagedFilesCountButtonProps) => (
  <FileCountToggle
    type="button"
    onClick={onClick}
    aria-label={`${count} staged file${count === 1 ? '' : 's'}, click to ${isOpen ? 'collapse' : 'expand'}`}
  >
    <FileText size={13} />
    <FileCountBadge>{count}</FileCountBadge>
    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
  </FileCountToggle>
);

const FileCountToggle = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  height: 28,
  padding: '0 8px',
  background: 'none',
  border: `1px solid ${theme.palette.gray.light1}`,
  borderRadius: 14,
  cursor: 'pointer',
  color: theme.palette.gray.dark1,
  fontSize: 13,
  fontFamily: theme.fonts.semiBold,
  transition: 'border-color 0.2s, color 0.2s',
  ':hover': {
    borderColor: theme.palette.sentiment.primary.main,
    color: theme.palette.sentiment.primary.main,
  },
}));

const FileCountBadge = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.sentiment.primary.main,
}));
